import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import useLocalizedPath from "src/hooks/useLocalizedPath"

import { selectGameConfig } from "src/store/common"
import { selectViewerId } from "src/store/game"
import {
  DEFENDERS_WANTED,
  HORDE_AT,
  HORDE_SIZE,
  LEARNER_AT,
  STEPS,
  defenceNumbers,
  farmStaff,
  foodPerRound,
  foundersWanted,
  soldierPrice,
  soldierUpkeep,
  upgradeCost,
  workersPerRound,
} from "src/tutorial/scenario"
import * as server from "src/tutorial/server"
import { foodIcon } from "src/utils/resources"

import "./TutorialCoach.css"

/* The only thing basic training draws that the game does not.

   Everything else on screen is `views/Game` — so this has exactly one job, and the shape it takes
   follows from that: say what to press next, and get out of the way. It is a strip along the
   bottom rather than a panel, because a panel is chrome a player would have to learn and then
   unlearn; the strip sits over the map's lower edge, where the game itself puts nothing but the
   reaction button, and the steps are a progress line rather than a menu.

   It reads the run through `server.subscribe` rather than the redux store. The store holds the
   *game*, which is deliberately indistinguishable from a live one; which step the lesson is on and
   whether its move has been made are the tutorial's business and nothing the game view should be
   able to see. */

/* The numbers a step quotes, every one read off the config or the board; the coach writes none of
   its own. `food` is humanity's glyph from the same helper the top bar and the upkeep rows use,
   so the coach's "3 🌽 a round" is the same picture as the chip beside it. */
const numbersFor = (step, gameConfig, game, viewerId) => {
  const cell = game?.metadata?.cells?.[LEARNER_AT]
  const holding = cell && Object.values(cell.metadata_by_user || {})[0]
  const food = foodIcon("human")
  switch (step) {
    case "commandCenter":
      return {
        cost: upgradeCost(gameConfig, "command_center", holding?.buildings?.command_center?.level ?? 1),
        amount: workersPerRound(gameConfig, 1),
        at: LEARNER_AT,
      }
    case "cultivationCenter":
      return {
        cost: upgradeCost(gameConfig, "cultivation_center", holding?.buildings?.cultivation_center?.level ?? 1),
        amount: foodPerRound(gameConfig, 1),
        staff: farmStaff(gameConfig, 1),
        food,
        at: LEARNER_AT,
      }
    case "defend":
      return {
        count: DEFENDERS_WANTED,
        horde: HORDE_SIZE,
        price: soldierPrice(gameConfig),
        upkeep: soldierUpkeep(gameConfig),
        food,
        at: LEARNER_AT,
      }
    case "battle":
      return { ...defenceNumbers(), horde: HORDE_SIZE, at: LEARNER_AT }
    case "attack":
      return { at: HORDE_AT, from: LEARNER_AT, food, founders: foundersWanted(gameConfig, game, viewerId) }
    case "settle":
      return { at: HORDE_AT, cost: foundersWanted(gameConfig, game, viewerId) }
    default:
      return {}
  }
}

const TutorialCoach = () => {
  const { t } = useTranslation()
  const localized = useLocalizedPath()
  const gameConfig = useSelector(selectGameConfig)
  const viewerId = useSelector(selectViewerId)
  const [run, setRun] = useState(() => server.snapshot())
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => server.subscribe(setRun), [])

  /* Every new sentence opens the strip again — a new step, or the same step's move made — so the
     player is never left with a closed coach and a sentence they have not seen. */
  const sentence = run && !run.finished ? `${run.step}:${run.acted}` : null
  useEffect(() => setCollapsed(false), [sentence])

  if (!run) return null

  if (run.finished) {
    return (
      <div className="coach done" role="status">
        <div className="coach-body">
          <p className="coach-step">{t("tutorial.outro.title")}</p>
          <p className="coach-line">{t("tutorial.outro.body")}</p>
        </div>
        <div className="coach-actions">
          <Link to={localized("/")} className="coach-exit">
            {t("tutorial.outro.play")}
          </Link>
          <Link to={localized("/how-to-play")} className="coach-quiet">
            {t("tutorial.outro.manual")}
          </Link>
        </div>
      </div>
    )
  }

  const step = run.step
  const values = numbersFor(step, gameConfig, run.game, viewerId)
  // The battle step turns no round, so once the report has been opened the coach carries the way on.
  const canContinue = step === "battle" && run.acted
  /* The × puts the strip away and "Steps" brings the sentence back. It gates nothing (owner,
     2026-10-03): what a new step waits for is the other sheets being closed (`server.markClear`). */
  const close = () => setCollapsed(true)

  return (
    <div
      className={`coach${collapsed ? " collapsed" : ""}${canContinue ? " reading" : ""}`}
      role="status"
    >
      {/* One header line — where the lesson is, what this step is called, and the way to put it
          away — and the sentence under it at the strip's full width. The pips used to be a column
          of their own beside the sentence, which on a phone left the words a third of the box. */}
      <div className="coach-head">
        <ol className="coach-steps" aria-label={t("tutorial.objective")}>
          {STEPS.map((id, position) => (
            <li
              key={id}
              className={`coach-pip${position === run.stepIndex ? " current" : ""}${
                position < run.stepIndex ? " done" : ""
              }`}
              title={t(`tutorial.steps.${id}.name`)}
            >
              {position < run.stepIndex ? "✓" : position + 1}
            </li>
          ))}
        </ol>
        <p className="coach-step">{t(`tutorial.steps.${step}.name`)}</p>
        {collapsed ? (
          <button type="button" className="coach-collapse" onClick={() => setCollapsed(false)} aria-expanded={false}>
            {t("tutorial.show")}
          </button>
        ) : (
          <button
            type="button"
            className="coach-close"
            onClick={close}
            aria-expanded
            aria-label={t("tutorial.close")}
            title={t("tutorial.close")}
          >
            ×
          </button>
        )}
      </div>

      <div className="coach-body">
        {/* What the round that just turned did, when it did something worth a sentence. Above the
            instruction, because it is the answer to what the player just pressed. */}
        {run.outcome && <p className="coach-outcome">{t(`tutorial.report.${run.outcome}`, values)}</p>}
        <p className="coach-line">
          {t(run.acted ? `tutorial.steps.${step}.done` : `tutorial.steps.${step}.do`, values)}
        </p>
        {canContinue && (
          <button type="button" className="coach-continue" onClick={() => server.advance()}>
            {t("tutorial.continue")}
          </button>
        )}
      </div>
    </div>
  )
}

export default TutorialCoach
